import React, { useEffect, useState, memo } from 'react';
import { Text, Button } from '@cuemath/web-comps';
import { TextInput } from '@cuemath/comps';
import { Separator, Spinner } from '@cuemath/comps';

import { useFunnelAllPagesDetail, useFunnelTimeseries } from '../api/get-funnel';
import KpiCards from '../components/kpi-cards';
import ChipGroup from '../components/chips';
import CountrySelect from '../components/country-select';
import RangePicker from '../components/range-picker';
import FunnelTable, { buildPageLandingColumns } from '../components/funnel-table';
import FunnelBarChart from '../components/charts/funnel-bar-chart';
import {
  Card,
  SectionLabel,
  ChartBox,
  StateBlock,
  FiltersBar,
  FilterField,
  ActionsField,
  ChipGroupsRow,
  FieldLabelText,
  LoadingOverlay,
  UI,
} from '../styled-comps';
import {
  METRICS,
  METRIC_LABELS,
  METRIC_COLORS,
  EMPTY_MESSAGE,
  REGION_OPTIONS,
  USER_TYPE_OPTIONS,
  ANALYSIS_MIN_DATE,
} from '../constants';
import { asRows, metricFields, sumRows, formatMonthLabel, buildRangeParams } from '../helpers';
import { useSettledKey } from '../hooks/use-settled-key';

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const defaultDraft = () => ({
  start: ANALYSIS_MIN_DATE,
  end: today(),
  page: '',
  regions: [],
  countries: [],
  userTypes: [],
});

// Query for one page: the lead counts when the page matches EITHER its signup
// page OR its signup-session landing page (the "why this category" column) —
// backend `analyze_path` applies the OR across both columns.
const buildQuery = applied =>
  buildRangeParams(applied.start, applied.end, {
    regions: applied.regions,
    countries: applied.countries,
    userTypes: applied.userTypes,
    analyzePath: applied.page.trim(),
  });

// Page Analysis tab: date range (from Apr 2025) + page path + region + user
// type -> KPI cards, the (signup x landing) pair table where either column
// matches the page, and a month-on-month funnel graph. Queries fire on Apply.
const PageAnalysis = memo(() => {
  const [draft, setDraft] = useState(defaultDraft);
  const [applied, setApplied] = useState(defaultDraft);

  const {
    data: pairsData,
    get: getPairs,
    isProcessing: pairsLoading,
    error: pairsError,
  } = useFunnelAllPagesDetail();
  const {
    data: monthsData,
    get: getMonths,
    isProcessing: monthsLoading,
    error: monthsError,
  } = useFunnelTimeseries();

  const hasPage = !!applied.page.trim();
  const appliedQuery = hasPage ? buildQuery(applied) : null;
  const queryKey = appliedQuery ? JSON.stringify(appliedQuery) : '';

  useEffect(() => {
    if (appliedQuery) {
      getPairs('wm-page-analysis-pairs', appliedQuery);
      getMonths('wm-page-analysis-months', appliedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  const patchDraft = patch => setDraft(prev => ({ ...prev, ...patch }));
  const handleApply = () => setApplied(draft);
  const handleReset = () => {
    const defaults = defaultDraft();
    setDraft(defaults);
    setApplied(defaults);
  };
  const hasUnappliedChanges = JSON.stringify(draft) !== JSON.stringify(applied);

  // True only once each response reflects the applied query — so the "<page>"
  // label, cards, table and graph never render the previous page's numbers for
  // the render between Apply and the store swapping in the new query's resource.
  const freshPairs = useSettledKey(queryKey, pairsLoading, pairsError);
  const freshMonths = useSettledKey(queryKey, monthsLoading, monthsError);

  // ── table + cards: (signup x landing) pairs where either side matches ──
  const pairRows = (freshPairs ? asRows(pairsData) : [])
    .map(row => ({ label: row.page, landing: row.landing_page, ...metricFields(row) }))
    .sort((a, b) => b[METRICS.QL] - a[METRICS.QL]);
  const totals = sumRows(pairRows);

  // ── graph: month-on-month funnel for the matched leads (sum over channels) ──
  const byMonth = {};
  asRows(freshMonths ? monthsData : []).forEach(row => {
    const bucket = byMonth[row.month] || (byMonth[row.month] = metricFields({}));
    Object.keys(metricFields(row)).forEach(key => {
      bucket[key] += Number(row[key] || 0);
    });
  });
  const monthKeys = Object.keys(byMonth).sort();
  const graphDatasets = [
    METRICS.QL,
    METRICS.TRIAL_SCHEDULED,
    METRICS.TRIAL_DONE,
    METRICS.ENROLLED,
  ].map(metric => ({
    label: METRIC_LABELS[metric],
    color: METRIC_COLORS[metric],
    data: monthKeys.map(month => byMonth[month][metric] || 0),
  }));

  const isFetching = hasPage && (pairsLoading || monthsLoading);

  return (
    <>
      {isFetching && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <SectionLabel>
        Analyze a single page — a lead counts when the page is its signup page OR its landing page
      </SectionLabel>
      <FiltersBar>
        <FilterField $width="250px">
          <FieldLabelText variant="eyebrow2">Date range</FieldLabelText>
          <RangePicker
            start={draft.start}
            end={draft.end}
            minDate={ANALYSIS_MIN_DATE}
            maxDate={today()}
            onChange={(start, end) => patchDraft({ start, end })}
          />
        </FilterField>
        <FilterField $width="230px">
          <FieldLabelText variant="eyebrow2">Page path</FieldLabelText>
          <TextInput
            value={draft.page}
            onChange={value => patchDraft({ page: value })}
            placeholder="e.g. summer-math or /pricing/"
          />
        </FilterField>
        <FilterField $width="auto">
          <ChipGroupsRow>
            <ChipGroup
              label="Region"
              options={REGION_OPTIONS}
              value={draft.regions}
              onChange={regions => patchDraft({ regions })}
              multi
            />
            <ChipGroup
              label="User type"
              options={USER_TYPE_OPTIONS}
              value={draft.userTypes}
              onChange={userTypes => patchDraft({ userTypes })}
              multi
            />
          </ChipGroupsRow>
        </FilterField>
        {!!draft.regions.length && (
          <CountrySelect
            regions={draft.regions}
            value={draft.countries}
            onChange={countries => patchDraft({ countries })}
          />
        )}
        <ActionsField>
          <Button
            size="small"
            label={hasUnappliedChanges ? 'Apply *' : 'Apply'}
            onClick={handleApply}
          />
          <Button size="small" variant="tertiaryWithUnderline" label="Reset" onClick={handleReset} />
        </ActionsField>
      </FiltersBar>

      {!hasPage ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>
            Enter a page path (e.g. summer-math or /pricing/) and press Apply
          </Text>
        </StateBlock>
      ) : pairsError ? (
        <StateBlock>
          <Text variant="body1" color={UI.down}>{pairsError}</Text>
        </StateBlock>
      ) : (pairsLoading || !freshPairs) && !pairRows.length ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>Loading page metrics…</Text>
        </StateBlock>
      ) : (
        <>
          <SectionLabel>{`"${applied.page.trim()}" — funnel totals for matched leads`}</SectionLabel>
          <KpiCards totals={totals} compareTotals={null} />

          <SectionLabel>
            {`Matched (signup x landing) pairs — "${applied.page.trim()}" appears in either column; rows sum to the totals above`}
          </SectionLabel>
          <FunnelTable
            data={pairRows}
            columns={buildPageLandingColumns()}
            loading={pairsLoading}
            defaultSorted={[{ id: METRICS.QL, desc: true }]}
            emptyMessage={EMPTY_MESSAGE}
            showTotals
          />

          <SectionLabel>Month on month — matched leads</SectionLabel>
          <Card>
            {monthsError ? (
              <Text variant="body2" color={UI.down}>{monthsError}</Text>
            ) : (
              <ChartBox $height={320}>
                {(monthsLoading || !freshMonths) && !monthKeys.length ? (
                  <Text variant="body3" color={UI.muted}>Loading graph…</Text>
                ) : monthKeys.length ? (
                  <FunnelBarChart
                    labels={monthKeys.map(formatMonthLabel)}
                    datasets={graphDatasets}
                    stacked={false}
                    showLegend
                  />
                ) : (
                  <Text variant="body3" color={UI.muted}>{EMPTY_MESSAGE}</Text>
                )}
              </ChartBox>
            )}
            <Separator height={6} transparent />
            <Text variant="caption" color={UI.muted}>
              Grouped by lead month over the selected date range, all four funnel metrics.
            </Text>
          </Card>
        </>
      )}
    </>
  );
});

export default PageAnalysis;
